import { BODY_FONT, TITLE_FONT, SHEET_W, STRIP_H, COL_GAP } from './rateChartConfig';

// The poster itself — drawn at a fixed 595px width (A4 at 72dpi) and only ever scaled from outside.
// Colours come from the chosen theme, so they are inline styles on purpose (a printed document, like the
// invoice templates); the editor chrome around it uses Tailwind.
//
// `m` is the whole display model built by the editor:
//   th / tk / d   theme, layout tokens, density      cols, H   column count and page height in px
//   logoSrc, qrSrc, showYear, showWm
//   text          { kicker, title, subtitle, note, shop, phone, addr, year, phoneLabel, scanMe, taka, negotiable, pageLabel(n, total) }
// and a row is { id, name, price, hasPrice } with `price` already localized.

const inkMuted = '#7A716A';

export function SheetHeader({ m }) {
    const { th, tk, text, logoSrc, showYear } = m;
    return (
        <div
            data-rc-head
            style={{ background: th.hdr, color: th.hdrInk, padding: tk.hdrPad, position: 'relative', borderBottom: tk.hdrBdB, textAlign: tk.hdrTextAlign }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    {logoSrc && (
                        <div style={{ width: 40, height: 40, borderRadius: 9, flex: 'none', background: '#fff', padding: 3, boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img src={logoSrc} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }} />
                        </div>
                    )}
                    <div style={{ fontSize: 13.5, fontWeight: 600, opacity: 0.92, letterSpacing: tk.kickerLs, textTransform: tk.kickerTf }}>{text.kicker}</div>
                </div>
                {showYear && text.year && (
                    <div style={{ background: th.acc, color: th.yearInk, fontSize: 12.5, fontWeight: 700, padding: '3px 13px', borderRadius: tk.badgeRadius, flex: 'none' }}>{text.year}</div>
                )}
            </div>
            <div style={{ fontFamily: TITLE_FONT, fontSize: tk.titleFs, fontWeight: 700, lineHeight: 1.15, letterSpacing: tk.titleLs }}>{text.title}</div>
            <div style={{ display: tk.ornShow, justifyContent: tk.ornJustify, gap: 7, marginTop: 8, color: th.acc, fontSize: 10, letterSpacing: '.3em' }}>◆◆◆</div>
            <div style={{ fontSize: 14, opacity: 0.88, marginTop: 5, fontStyle: tk.subStyle }}>{text.subtitle}</div>
            <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: tk.hdrBarH, background: th.acc }} />
        </div>
    );
}

export function SheetRow({ m, row, index }) {
    const { th, tk, d, text } = m;
    return (
        <div
            data-rc-row
            style={{
                display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12,
                padding: `${d.padV}px ${d.padH}px`,
                background: tk.zebra && index % 2 === 0 ? tk.zebra : 'transparent',
                borderRadius: tk.rowRadius, borderBottom: tk.rowBdB,
            }}
        >
            <div style={{ flex: 1, minWidth: 0, fontSize: d.nameFs, fontWeight: 500, color: '#2A2420', lineHeight: 1.4, overflowWrap: 'anywhere' }}>{row.name}</div>
            <div style={{ flex: 'none', whiteSpace: 'nowrap' }}>
                <span
                    style={{
                        display: 'inline-block', minWidth: 22, textAlign: 'right',
                        fontSize: row.hasPrice ? d.priceFs : d.unitFs + 1,
                        fontWeight: row.hasPrice ? 700 : 600,
                        fontStyle: row.hasPrice ? 'normal' : 'italic',
                        color: row.hasPrice ? th.price : inkMuted,
                    }}
                >
                    {row.hasPrice ? row.price : text.negotiable}
                </span>
                {row.hasPrice && <span style={{ fontSize: d.unitFs, fontWeight: 600, color: inkMuted, marginLeft: 3 }}>{text.taka}</span>}
            </div>
        </div>
    );
}

export function SheetBody({ m, columns }) {
    const { tk, d, cols } = m;
    return (
        <div style={{ flex: 1, padding: `${d.bodyPad[0]}px ${d.bodyPad[1]}px ${d.bodyPad[2]}px` }}>
            <div style={{ background: tk.wrapBg, border: tk.wrapBd, borderRadius: tk.wrapRadius, padding: tk.wrapPad }}>
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, columnGap: COL_GAP, alignItems: 'start' }}>
                    {columns.map((rows, ci) => (
                        <div key={ci} style={{ minWidth: 0 }}>
                            {rows.map((row, i) => <SheetRow key={row.id} m={m} row={row} index={i} />)}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export function SheetFooter({ m }) {
    const { th, tk, text, qrSrc } = m;
    return (
        <div data-rc-foot style={{ display: 'flow-root' }}>
            {text.note && (
                <div style={{ margin: '4px 30px 0', padding: '10px 14px', background: th.note, borderLeft: `4px solid ${th.acc}`, borderRadius: '0 6px 6px 0' }}>
                    <div style={{ fontSize: 12.5, color: tk.noteInk, fontWeight: 500, lineHeight: 1.55, overflowWrap: 'anywhere' }}>{text.note}</div>
                </div>
            )}
            <div style={{ background: tk.footBg, color: tk.footInk, padding: '16px 30px 22px', marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
                <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <div style={{ fontFamily: TITLE_FONT, fontSize: 19, fontWeight: 700, lineHeight: 1.25, overflowWrap: 'anywhere' }}>{text.shop}</div>
                    {text.phone && <div style={{ fontSize: 13.5, opacity: 0.92 }}><span>{text.phoneLabel} </span><span>{text.phone}</span></div>}
                    {text.addr && <div style={{ fontSize: 13.5, opacity: 0.92, overflowWrap: 'anywhere' }}>{text.addr}</div>}
                </div>
                {qrSrc && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flex: 'none' }}>
                        <div style={{ width: 64, height: 64, borderRadius: 6, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img src={qrSrc} alt="" width={56} height={56} style={{ display: 'block', imageRendering: 'pixelated' }} />
                        </div>
                        <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.92 }}>{text.scanMe}</div>
                    </div>
                )}
            </div>
        </div>
    );
}

/** One sheet of paper. `columns` = the rows of each column on this page. Fixed height, so exports are always the paper's shape. */
export function SheetPage({ m, columns, index, total, preview = false }) {
    const { th, tk, text } = m;
    const last = index === total - 1;
    return (
        <div
            data-poster-page
            style={{
                width: SHEET_W, height: m.H, background: tk.bodyBg, position: 'relative', overflow: 'hidden',
                display: 'flex', flexDirection: 'column', border: tk.pageBd, padding: tk.pagePad, boxSizing: 'border-box', flex: 'none',
                fontFamily: BODY_FONT, color: '#1C1815', boxShadow: preview ? '0 10px 40px rgba(40,28,18,.14)' : 'none',
            }}
        >
            {index === 0 ? <SheetHeader m={m} /> : <div style={{ height: STRIP_H, background: th.acc, flex: 'none' }} />}
            <SheetBody m={m} columns={columns} />
            {last && <SheetFooter m={m} />}
            {total > 1 && (
                <div style={{ position: 'absolute', left: 0, right: 0, bottom: 5, textAlign: 'center', fontSize: 10.5, color: last ? tk.footInk : '#8A8077', opacity: 0.75 }}>
                    {text.pageLabel(index + 1, total)}
                </div>
            )}
            {m.showWm && text.shop && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                    <div style={{ fontFamily: TITLE_FONT, fontSize: 60, fontWeight: 700, color: 'rgba(28,24,21,.05)', transform: 'rotate(-24deg)', whiteSpace: 'nowrap' }}>{text.shop}</div>
                </div>
            )}
        </div>
    );
}

/** Off-screen copy of the blocks whose real height decides the page breaks — measured, never guessed, so Bangla wrapping is exact. */
export function SheetRuler({ m, rows, rulerRef }) {
    const { contentW, colW } = m.metrics;
    return (
        <div
            ref={rulerRef}
            aria-hidden="true"
            style={{ position: 'fixed', left: -100000, top: 0, width: SHEET_W, visibility: 'hidden', pointerEvents: 'none', fontFamily: BODY_FONT }}
        >
            <div style={{ width: contentW }}><SheetHeader m={m} /></div>
            <div style={{ width: contentW }}><SheetFooter m={m} /></div>
            <div style={{ width: colW }}>
                {rows.map((row, i) => <SheetRow key={row.id} m={m} row={row} index={i} />)}
            </div>
        </div>
    );
}
